import {
    type ComponentProps,
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    HomeIcon,
    BellIcon,
    FileIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "Tabs";

const USAGE_CODE = `const [tab, setTab] = useState("overview");

<Tabs
    onValueChange={setTab}
    value={tab}
>
    <Tabs.List
        title="Project tabs"
    >
        <Tabs.Trigger
            title="Overview"
            value="overview"
        />
        <Tabs.Trigger
            title="Tasks"
            value="tasks"
            count={24}
        />
        <Tabs.Trigger
            title="Invoices"
            value="invoices"
            isDisabled={true}
        />
    </Tabs.List>
    <Tabs.Content
        value="overview"
    >
        <OverviewPanel/>
    </Tabs.Content>
    <Tabs.Content
        value="tasks"
        isKeepMounted={true}
    >
        <TasksPanel/>
    </Tabs.Content>
</Tabs>`;

const RADIO_CODE = `<Tabs
    listRole="radiogroup"
    onValueChange={setTheme}
    variant="segment"
    value={theme}
>
    <Tabs.List
        title="Theme"
    >
        <Tabs.Trigger
            title="Light"
            value="light"
        />
        <Tabs.Trigger
            title="Dark"
            value="dark"
        />
    </Tabs.List>
</Tabs>`;

const OVERFLOW_CODE = `<Tabs
    overflowBehaviour="menu"
>
    <Tabs.List
        title="Project tabs"
    >
        {
            tabs.map((tab) => <Tabs.Trigger
                title={tab.title}
                value={tab.value}
                key={tab.value}
            />)
        }
    </Tabs.List>
</Tabs>`;

const TAB_KEYS = [
    "overview",
    "tasks",
    "reports"
] as const;

const NARROW_KEYS = [
    "overview",
    "tasks",
    "reports",
    "invoices",
    "team",
    "billing",
    "security",
    "settings"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const VARIANTS = [
    "line",
    "segment"
] as const;

const OVERFLOW_BEHAVIOURS = [
    "scroll",
    "menu",
    "auto"
] as const;

const THEME_KEYS = [
    "light",
    "dark",
    "system"
] as const;

type TabKey = typeof NARROW_KEYS[number];

type TabsDemoProps = Omit<ComponentProps<typeof Tabs>, "children">;

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: "100%",
    gap: 8
};

const ROW_STYLE: CSSProperties = {
    alignItems: "flex-start",
    flexWrap: "wrap",
    display: "flex",
    gap: 24
};

const NARROW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: 260
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("tabsPage.iconsTitle"),
        id: "icons"
    },
    {
        title: t("tabsPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("tabsPage.overflowTitle"),
        id: "overflow"
    },
    {
        title: t("tabsPage.activationTitle"),
        id: "activation"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TabsPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        tab,
        setTab
    ] = useState<string>("overview");

    const [
        theme,
        setTheme
    ] = useState<string>("light");

    const tabTitles: Record<TabKey, string> = {
        invoices: t("tabsPage.invoices"),
        overview: t("tabsPage.overview"),
        security: t("tabsPage.security"),
        settings: t("tabsPage.settings"),
        billing: t("tabsPage.billing"),
        reports: t("tabsPage.reports"),
        tasks: t("tabsPage.tasks"),
        team: t("tabsPage.team")
    };

    const themeTitles: Record<typeof THEME_KEYS[number], string> = {
        system: t("tabsPage.themeSystem"),
        light: t("tabsPage.themeLight"),
        dark: t("tabsPage.themeDark")
    };

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    const renderPanel = (key: TabKey) => <Tabs.Content
        value={key}
        key={key}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {t("tabsPage.panelOf", [
                tabTitles[key]
            ])}
        </Text>
    </Tabs.Content>;

    const renderList = (props: TabsDemoProps, keys: ReadonlyArray<TabKey> = TAB_KEYS) => <Tabs
        {...props}
    >
        <Tabs.List
            title={t("tabsPage.listTitle")}
        >
            {
                keys.map((key) => <Tabs.Trigger
                    title={tabTitles[key]}
                    value={key}
                    key={key}
                />)
            }
        </Tabs.List>
    </Tabs>;

    const renderPanelTabs = (props: TabsDemoProps) => <Tabs
        {...props}
    >
        <Tabs.List
            title={t("tabsPage.listTitle")}
        >
            {
                TAB_KEYS.map((key) => <Tabs.Trigger
                    title={tabTitles[key]}
                    value={key}
                    key={key}
                />)
            }
        </Tabs.List>
        {TAB_KEYS.map(renderPanel)}
    </Tabs>;

    return <Article>
        <ComponentHeader
            lead={t("tabsPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("tabsPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("tabsPage.overviewCaption", [
                    JSON.stringify(tab)
                ])}
                isStacked={true}
            >
                <Tabs
                    onValueChange={setTab}
                    value={tab}
                >
                    <Tabs.List
                        title={t("tabsPage.listTitle")}
                    >
                        <Tabs.Trigger
                            title={t("tabsPage.overview")}
                            value="overview"
                        />
                        <Tabs.Trigger
                            title={t("tabsPage.tasks")}
                            value="tasks"
                            count={24}
                        />
                        <Tabs.Trigger
                            title={t("tabsPage.invoices")}
                            isDisabled={true}
                            value="invoices"
                        />
                    </Tabs.List>
                    <Tabs.Content
                        value="overview"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("tabsPage.overviewPanel")}
                        </Text>
                    </Tabs.Content>
                    <Tabs.Content
                        value="tasks"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("tabsPage.tasksPanel")}
                        </Text>
                    </Tabs.Content>
                </Tabs>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("tabsPage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => renderCell(`variant="${variant}"`, renderList({
                        variant
                    })))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("tabsPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderCell(`size="${size}"`, <div
                        style={ROW_STYLE}
                    >
                        {renderList({
                            variant: "line",
                            size
                        })}
                        {renderList({
                            variant: "segment",
                            size
                        })}
                    </div>))
                }
            </Preview>
        </Section>
        <Section
            title={t("tabsPage.iconsTitle")}
            id="icons"
        >
            <Paragraph>
                {t("tabsPage.iconsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Tabs
                    overflowBehaviour="scroll"
                    defaultValue="home"
                >
                    <Tabs.List
                        title={t("tabsPage.listTitle")}
                    >
                        <Tabs.Trigger
                            title={t("sample.home")}
                            icon={HomeIcon}
                            value="home"
                        />
                        <Tabs.Trigger
                            title={t("tabsPage.inbox")}
                            countType="danger"
                            icon={FileIcon}
                            value="inbox"
                            count={5}
                        />
                        <Tabs.Trigger
                            title={t("tabsPage.updates")}
                            icon={UserIcon}
                            value="updates"
                            count={120}
                        />
                        <Tabs.Trigger
                            title={t("tabsPage.archive")}
                            icon={FileIcon}
                            value="archive"
                            count="1.248"
                        />
                        <Tabs.Trigger
                            accessibilityLabel={t("tabsPage.notifications")}
                            value="notifications"
                            icon={BellIcon}
                        />
                    </Tabs.List>
                </Tabs>
            </Preview>
        </Section>
        <Section
            title={t("tabsPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("tabsPage.layoutText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("orientation=\"vertical\"", renderPanelTabs({
                        orientation: "vertical"
                    }))
                }
                {
                    renderCell("spreadBehaviour=\"stretch\"", renderList({
                        spreadBehaviour: "stretch"
                    }))
                }
            </Preview>
        </Section>
        <Section
            title={t("tabsPage.overflowTitle")}
            id="overflow"
        >
            <Paragraph>
                {t("tabsPage.overflowText")}
            </Paragraph>
            <Preview
                caption={t("tabsPage.overflowCaption")}
                isStacked={true}
            >
                {
                    OVERFLOW_BEHAVIOURS.map((overflowBehaviour) => renderCell(`overflowBehaviour="${overflowBehaviour}"`, <div
                        style={NARROW_STYLE}
                    >
                        {renderList({
                            overflowBehaviour
                        }, NARROW_KEYS)}
                    </div>))
                }
            </Preview>
            <CodeBlock
                code={OVERFLOW_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("tabsPage.activationTitle")}
            id="activation"
        >
            <Paragraph>
                {t("tabsPage.activationText")}
            </Paragraph>
            <Preview
                caption={t("tabsPage.activationCaption", [
                    JSON.stringify(theme)
                ])}
                isStacked={true}
            >
                {
                    renderCell("activationMode=\"automatic\"", renderPanelTabs({
                        activationMode: "automatic"
                    }))
                }
                {
                    renderCell("activationMode=\"manual\"", renderPanelTabs({
                        activationMode: "manual"
                    }))
                }
                {
                    renderCell("listRole=\"radiogroup\"", <Tabs
                        onValueChange={setTheme}
                        listRole="radiogroup"
                        variant="segment"
                        value={theme}
                    >
                        <Tabs.List
                            title={t("tabsPage.themeTitle")}
                        >
                            {
                                THEME_KEYS.map((key) => <Tabs.Trigger
                                    title={themeTitles[key]}
                                    value={key}
                                    key={key}
                                />)
                            }
                        </Tabs.List>
                    </Tabs>)
                }
            </Preview>
            <CodeBlock
                code={RADIO_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("tabsPage.a11yRoles"),
                    t("tabsPage.a11yTabStop"),
                    t("tabsPage.a11yPanel"),
                    t("tabsPage.a11yKeys"),
                    t("tabsPage.a11yOverflow"),
                    t("tabsPage.a11yActivation"),
                    t("tabsPage.a11yRadio"),
                    t("tabsPage.a11yDisabled"),
                    t("tabsPage.a11yCount"),
                    t("tabsPage.a11yIconOnly"),
                    t("tabsPage.a11yMotion"),
                    t("tabsPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("tabsPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("tabsPage.propDefaultValue"),
                        name: "defaultValue",
                        type: "string"
                    },
                    {
                        description: t("tabsPage.propOnValueChange"),
                        type: "(value: string) => void",
                        name: "onValueChange"
                    },
                    {
                        description: t("tabsPage.propVariant"),
                        type: "\"line\" | \"segment\"",
                        defaultValue: "\"line\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("tabsPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("tabsPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("tabsPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("tabsPage.propOverflowBehaviour"),
                        type: "\"auto\" | \"scroll\" | \"menu\"",
                        defaultValue: "\"auto\"",
                        name: "overflowBehaviour"
                    },
                    {
                        description: t("tabsPage.propActivationMode"),
                        type: "\"automatic\" | \"manual\"",
                        defaultValue: "\"automatic\"",
                        name: "activationMode"
                    },
                    {
                        description: t("tabsPage.propListRole"),
                        type: "\"tablist\" | \"radiogroup\"",
                        defaultValue: "\"tablist\"",
                        name: "listRole"
                    },
                    {
                        description: t("tabsPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tabsPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("tabsPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tabsPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Tabs.List
                </InlineCode>
                {" "}
                {t("tabsPage.partListText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tabsPage.propListTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "keyof ContainerContentColors | keyof ProjectColorPalette | string",
                        description: t("tabsPage.propListCustomFadeColor"),
                        name: "customFadeColor"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tabsPage.listNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Tabs.Trigger
                </InlineCode>
                {" "}
                {t("tabsPage.partTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tabsPage.propTriggerValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("tabsPage.propTriggerTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("tabsPage.propTriggerIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("tabsPage.propTriggerCount"),
                        type: "number | string",
                        name: "count"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("tabsPage.propTriggerCountType"),
                        defaultValue: "\"neutral\"",
                        name: "countType"
                    },
                    {
                        description: t("tabsPage.propTriggerIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("tabsPage.propTriggerAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tabsPage.triggerNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Tabs.Content
                </InlineCode>
                {" "}
                {t("tabsPage.partContentText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tabsPage.propContentValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("tabsPage.propContentIsKeepMounted"),
                        defaultValue: "false",
                        name: "isKeepMounted",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tabsPage.contentNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TabsPage;
